import { useEffect, useState } from 'react'
import type { ProductAlias } from '@domain/product'
import { Dobra } from '@ui/shell/Dobra'
import { useFinanceRepositories } from '@ui/finance/finance-context'

type Props = { productId: string }

/**
 * Os nomes e códigos com que este produto já apareceu nas notas.
 *
 * ## Ela existe para AUDITAR o aprendizado, não para enfeitar
 *
 * A tabela por trás (`productMatches`) já funcionava e era **invisível**: a
 * casa vinha aprendendo apelidos desde a fase 8 e ninguém tinha como ver o que
 * foi aprendido. Um apelido errado — a linha de arroz que alguém vinculou ao
 * detergente com pressa — passa a resolver sozinho em toda nota seguinte, com
 * `autoApplicable: true`, e **em silêncio**. Ver é o que permite corrigir; por
 * isso cada linha tem como apagar.
 *
 * ## Loja + código valem mais que o texto, e a tela diz isso
 *
 * O nome varia com quem cadastrou o PDV — `COCA S AC LA 310ML` numa nota,
 * `COCA ZERO LT 310` na outra. O código interno da loja não varia. Quando os
 * dois existem, o código é o que aparece em destaque; o texto fica como o que
 * é, uma grafia entre outras.
 *
 * Apelido aprendido antes de 2026-09-03 não tem loja nem código, e aparece só
 * com o texto — sem aviso de que falta algo, porque não falta: ele resolve
 * pelo texto, que é o degrau seguinte da cascata.
 */
export function TambemConhecidoComo({ productId }: Props) {
  const { productRepository } = useFinanceRepositories()
  const [apelidos, setApelidos] = useState<ProductAlias[] | null>(null)

  useEffect(() => {
    let vivo = true
    void productRepository
      .listAliases(productId)
      .then((lidos) => {
        if (vivo) setApelidos(lidos)
      })
      .catch((erro: unknown) => {
        // A ficha inteira funciona sem isto. Derrubar a tela do produto porque
        // a lista de apelidos não carregou seria trocar o principal pelo
        // acessório — mesma regra do catálogo em Lembretes.
        console.error('Produto: os apelidos não carregaram', erro)
        if (vivo) setApelidos([])
      })
    return () => {
      vivo = false
    }
  }, [productRepository, productId])

  // Enquanto carrega, e quando não há nenhum, a seção não existe: um cartão
  // vazio dizendo "nenhum apelido" ocupa o mesmo espaço visual de um cheio e
  // não informa nada que a ausência já não diga.
  if (apelidos === null || apelidos.length === 0) return null

  return (
    <Dobra titulo="Também conhecido como">
      <p className="mb-2 text-xs text-[var(--text-muted)]">
        Como este produto aparece nas notas. É o que faz a próxima nota resolver sozinha.
      </p>

      <ul>
        {apelidos.map((a) => (
          <li
            key={a.id}
            className="flex items-center gap-2 border-b border-[var(--border)] py-1 text-sm last:border-b-0"
          >
            <span className="min-w-0 flex-1">
              <span className="block truncate">
                {a.merchantName ?? a.merchantCnpj ?? 'Loja não identificada'}
              </span>
              <span className="block truncate text-xs text-[var(--text-faint)]">
                {a.normalizedText === '' ? 'sem texto legível' : a.normalizedText}
              </span>
            </span>

            {a.rawCode !== null && (
              <span className="shrink-0 font-mono text-xs tabular-nums text-[var(--brand)]">
                {a.rawCode}
              </span>
            )}

            <button
              type="button"
              className="alvo shrink-0 text-xs text-[var(--text-muted)]"
              aria-label={`Esquecer ${a.normalizedText === '' ? (a.rawCode ?? a.id) : a.normalizedText}`}
              onClick={() => {
                // Otimista, como o Monitorar: some da tela na hora, e a leitura
                // seguinte confirma. O pior caso de falha é o apelido voltar.
                setApelidos((atuais) => (atuais ?? []).filter((x) => x.id !== a.id))
                void productRepository.removeAlias(a.id).catch((erro: unknown) => {
                  console.error('Produto: o apelido não foi esquecido', erro)
                })
              }}
            >
              ✕
            </button>
          </li>
        ))}
      </ul>
    </Dobra>
  )
}
